import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { toast } from 'react-toastify';
import { EssCard, PriorityTag } from './components/helper';
import { supportApi } from '../../services';
import './styles/styles.scss';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_WORKING_HOURS = DAY_NAMES.map((_, i) => ({
    dayOfWeek: i,
    isWorking: i >= 1 && i <= 5,
    startTime: '09:00',
    endTime:   '18:00',
}));

const HourSpinner = ({ value, onChange, min = 1, max = 999 }) => {
    const inc = () => onChange(Math.min(max, (value || 0) + 1));
    const dec = () => onChange(Math.max(min, (value || 0) - 1));
    return (
        <div className="sla-spinner">
            <span className="sla-spinner-val">{value ?? 0} h</span>
            <div className="sla-spinner-btns">
                <button type="button" onClick={inc} tabIndex={-1}>▲</button>
                <button type="button" onClick={dec} tabIndex={-1}>▼</button>
            </div>
        </div>
    );
};

const DEFAULT_SLA = [
    { priority: 'Critical', firstResponseHrs: 1, resolutionHrs: 4,  escalateAfterHrs: 2  },
    { priority: 'High',     firstResponseHrs: 2, resolutionHrs: 8,  escalateAfterHrs: 4  },
    { priority: 'Medium',   firstResponseHrs: 4, resolutionHrs: 24, escalateAfterHrs: 8  },
    { priority: 'Low',      firstResponseHrs: 8, resolutionHrs: 48, escalateAfterHrs: 24 },
];

const DEFAULT_TOGGLES = { emailAlerts: true, autoEscalate: true, csatSurvey: true, weekendPause: false };

const NOTIF_ITEMS = [
    { key: 'emailAlerts',  label: 'Email alerts on SLA breach',       sub: 'Notify agent and supervisor' },
    { key: 'autoEscalate', label: 'Auto-escalate unassigned tickets',  sub: 'After 2 hours of no assignment' },
    { key: 'csatSurvey',   label: 'CSAT survey on ticket close',       sub: 'Send rating request to user' },
    { key: 'weekendPause', label: 'Weekend / holiday SLA pause',       sub: 'Pause SLA clock on non-working days' },
];

const SLA = ({ departmentId: propDeptId }) => {
    // propDeptId may be a comma-separated string "1,2,3" — use only the first valid ID
    const resolvedPropDeptId = propDeptId
        ? parseInt(String(propDeptId).split(",")[0]) || null
        : undefined;
    const [sla, setSla]                       = useState(DEFAULT_SLA);
    const [origSla, setOrigSla]               = useState(DEFAULT_SLA);
    const [workingDays, setWorkingDays]       = useState(DEFAULT_WORKING_HOURS);
    const [origDays, setOrigDays]             = useState(DEFAULT_WORKING_HOURS);
    const [toggles, setToggles]               = useState(DEFAULT_TOGGLES);
    const [agents, setAgents]                 = useState([]);
    const [rules, setRules]                   = useState([]);
    const [loading, setLoading]               = useState(false);
    const [saving, setSaving]                 = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [savingRules, setSavingRules]       = useState(false);
    const [departments, setDepartments]       = useState([]);
    const [selectedDept, setSelectedDept]     = useState(null);
    const [isSuperAdmin, setIsSuperAdmin]     = useState(false);

    useEffect(() => {
        supportApi.getDepartments()
            .then((res) => {
                const list = Array.isArray(res.data?.data) ? res.data.data : [];
                const opts = list.map((d) => ({ label: d.title, value: d.id }));
                setDepartments(opts);
                if (resolvedPropDeptId === undefined) {
                    if (opts.length > 1) { setIsSuperAdmin(true); setSelectedDept(opts[0].value); }
                    else if (opts.length === 1) { setIsSuperAdmin(false); setSelectedDept(opts[0].value); }
                } else {
                    setIsSuperAdmin(false);
                    setSelectedDept(resolvedPropDeptId ?? opts[0]?.value ?? null);
                }
            })
            .catch(() => {});
    }, []);  // eslint-disable-line

    const loadSlaData = useCallback((deptId) => {
        if (!deptId) return;
        setLoading(true);

        Promise.all([
            supportApi.getSlaConfig({ departmentId: deptId }),
            supportApi.getSlaSettings({ departmentId: deptId }),
            supportApi.getAgents({ departmentId: deptId }),
            supportApi.getAutoAssignmentRules({ departmentId: deptId }),
            supportApi.getCategories({ departmentId: deptId }),
            supportApi.getWorkingHours(deptId),
        ]).then(([slaRes, settingsRes, agentsRes, rulesRes, catRes, whRes]) => {

            const slaData = Array.isArray(slaRes.data?.data) && slaRes.data.data.length
                ? slaRes.data.data
                : DEFAULT_SLA.map((row) => ({ ...row, departmentId: deptId }));
            setSla(slaData);
            setOrigSla(slaData);

            setToggles(settingsRes.data?.data
                ? { ...DEFAULT_TOGGLES, ...settingsRes.data.data }
                : DEFAULT_TOGGLES
            );

            const agentList = (Array.isArray(agentsRes.data?.data) ? agentsRes.data.data : []).map((a) => ({
                label: `${a.user?.firstName ?? ''} ${a.user?.lastName ?? ''}`.trim() || a.user?.email || `Agent #${a.id}`,
                value: a.id,
            }));
            setAgents([...agentList, { label: 'Round Robin', value: 'rr' }]);

            const existingRules = Array.isArray(rulesRes.data?.data) ? rulesRes.data.data : [];
            const categories    = Array.isArray(catRes.data?.data)   ? catRes.data.data   : [];
            setRules(categories.map((cat) => {
                const ex = existingRules.find((r) => r.categoryId === cat.id);
                return ex
                    ? { ...ex, categoryName: cat.name, agentId: ex.ruleType === 'round_robin' ? 'rr' : ex.agentId }
                    : { categoryId: cat.id, categoryName: cat.name, agentId: null };
            }));

            // Working hours
            const rows = Array.isArray(whRes.data?.data) ? whRes.data.data : [];
            const merged = DEFAULT_WORKING_HOURS.map((def) => {
                const saved = rows.find((r) => r.dayOfWeek === def.dayOfWeek);
                return saved
                    ? { dayOfWeek: saved.dayOfWeek, isWorking: saved.isWorking, startTime: saved.startTime, endTime: saved.endTime }
                    : def;
            });
            setWorkingDays(merged);
            setOrigDays(merged);

        }).catch(() => {
            toast.error('Failed to load SLA data');
        }).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (resolvedPropDeptId !== undefined) {
            setIsSuperAdmin(false);
            setSelectedDept(resolvedPropDeptId);
        }
    }, [resolvedPropDeptId]);

    useEffect(() => {
        if (selectedDept) loadSlaData(selectedDept);
    }, [selectedDept, loadSlaData]);

    // ── Combined save: SLA matrix + working hours ──────────────────────────
    const onSaveSla = async () => {
        if (!selectedDept) { toast.warn('Please select a department first'); return; }
        setSaving(true);
        try {
            await Promise.all([
                supportApi.updateSlaConfig(sla.map((row) => ({ ...row, departmentId: selectedDept }))),
                supportApi.updateWorkingHours({ departmentId: selectedDept, days: workingDays }),
            ]);
            setOrigSla(sla);
            setOrigDays(workingDays);
            toast.success('SLA & working hours saved successfully');
        } catch {
            toast.error('Failed to save SLA settings');
        } finally { setSaving(false); }
    };

    const onReset = () => { setSla(origSla); setWorkingDays(origDays); };

    const onSaveSettings = async () => {
        if (!selectedDept) { toast.warn('Please select a department first'); return; }
        setSavingSettings(true);
        try {
            await supportApi.updateSlaSettings({ ...toggles, departmentId: selectedDept });
            toast.success('Notification settings saved');
        } catch {
            toast.error('Failed to save settings');
        } finally { setSavingSettings(false); }
    };

    const onSaveRules = async () => {
        if (!selectedDept) { toast.warn('Please select a department first'); return; }
        setSavingRules(true);
        try {
            await supportApi.updateAutoAssignmentRules(rules.map(({ categoryId, agentId }) => ({ categoryId, agentId })));
            toast.success('Assignment rules saved');
        } catch {
            toast.error('Failed to save rules');
        } finally { setSavingRules(false); }
    };

    const updateSlaRow = (i, key, value) =>
        setSla((p) => p.map((r, j) => j === i ? { ...r, [key]: value } : r));

    const updateRule = (i, agentId) =>
        setRules((p) => p.map((r, j) => j === i ? { ...r, agentId } : r));

    const updateDay = (i, key, val) =>
        setWorkingDays((p) => p.map((d, j) => j === i ? { ...d, [key]: val } : d));

    const selectedDeptLabel = departments.find((d) => d.value === selectedDept)?.label ?? '';

    return (
        <div className="sla-page">

            {isSuperAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
                    background: 'var(--blue-light, #eff6ff)', padding: '10px 14px',
                    borderRadius: 8, border: '1px solid #bfdbfe' }}>
                    <i className="pi pi-building" style={{ color: 'var(--blue)', fontSize: 16 }} />
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap' }}>
                        Viewing Department:
                    </label>
                    <Dropdown
                        value={selectedDept}
                        options={departments}
                        onChange={(e) => setSelectedDept(e.value)}
                        placeholder="Select department…"
                        style={{ minWidth: 200 }}
                    />
                    {selectedDeptLabel && (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                            All settings below apply to <strong>{selectedDeptLabel}</strong> only
                        </span>
                    )}
                </div>
            )}

            {!isSuperAdmin && selectedDeptLabel && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
                    background: 'var(--blue-light, #eff6ff)', padding: '8px 14px',
                    borderRadius: 8, border: '1px solid #bfdbfe' }}>
                    <i className="pi pi-building" style={{ color: 'var(--blue)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>
                        Department: {selectedDeptLabel}
                    </span>
                </div>
            )}

            {!selectedDept ? (
                <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>
                    Please select a department to manage SLA settings.
                </div>
            ) : (
                <div className="sla-grid">

                    {/* ── LEFT: SLA Matrix + Working Hours ── */}
                    <EssCard title="Response & Resolution SLA (Hours)" icon="pi-stopwatch">
                        {loading ? <Skeleton height="380px" /> : (
                            <>
                                {/* SLA Matrix */}
                                <table className="sla-matrix-table">
                                    <thead>
                                        <tr>
                                            <th>PRIORITY</th>
                                            <th>FIRST RESPONSE</th>
                                            <th>RESOLUTION</th>
                                            <th>ESCALATE AFTER</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sla.map((row, i) => (
                                            <tr key={row.priority}>
                                                <td><PriorityTag value={row.priority} /></td>
                                                <td>
                                                    <HourSpinner value={row.firstResponseHrs} max={72}
                                                        onChange={(v) => updateSlaRow(i, 'firstResponseHrs', v)} />
                                                </td>
                                                <td>
                                                    <HourSpinner value={row.resolutionHrs} max={168}
                                                        onChange={(v) => updateSlaRow(i, 'resolutionHrs', v)} />
                                                </td>
                                                <td>
                                                    <HourSpinner value={row.escalateAfterHrs} max={72}
                                                        onChange={(v) => updateSlaRow(i, 'escalateAfterHrs', v)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <Divider style={{ margin: '16px 0 12px' }} />

                                {/* Working Hours section */}
                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)',
                                    textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12,
                                    display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <i className="pi pi-clock" style={{ color: 'var(--blue)', fontSize: 13 }} />
                                    Support Days &amp; Working Hours
                                </div>

                                <div className="wh-grid">
                                    {workingDays.map((day, i) => (
                                        <div key={day.dayOfWeek} className={`wh-row${day.isWorking ? '' : ' wh-off'}`}>
                                            <div className="wh-day">{DAY_NAMES[day.dayOfWeek]}</div>
                                            <InputSwitch
                                                checked={day.isWorking}
                                                onChange={(e) => updateDay(i, 'isWorking', e.value)}
                                            />
                                            {day.isWorking ? (
                                                <div className="wh-times">
                                                    <input type="time" className="wh-time-input"
                                                        value={day.startTime}
                                                        onChange={(e) => updateDay(i, 'startTime', e.target.value)} />
                                                    <span className="wh-sep">to</span>
                                                    <input type="time" className="wh-time-input"
                                                        value={day.endTime}
                                                        onChange={(e) => updateDay(i, 'endTime', e.target.value)} />
                                                </div>
                                            ) : (
                                                <span className="wh-off-label">Non-working</span>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Single combined save */}
                                <div className="sla-actions" style={{ marginTop: 16 }}>
                                    <Button label="Reset" outlined severity="secondary"
                                        className="sla-btn-reset" onClick={onReset} />
                                    <Button label="Save SLA & Working Hours" icon="pi pi-save"
                                        loading={saving} className="sla-btn-save" onClick={onSaveSla} />
                                </div>
                            </>
                        )}
                    </EssCard>

                    {/* ── RIGHT column ── */}
                    <div className="sla-right-col">

                        <EssCard title="Notifications & Escalation" icon="pi-bell">
                            {loading ? <Skeleton height="140px" /> : (
                                <>
                                    {NOTIF_ITEMS.map(({ key, label, sub }, idx) => (
                                        <div key={key}>
                                            <div className="notif-row">
                                                <div>
                                                    <div className="notif-label">{label}</div>
                                                    <div className="notif-sub">{sub}</div>
                                                </div>
                                                <InputSwitch
                                                    checked={!!toggles[key]}
                                                    onChange={(e) => setToggles((p) => ({ ...p, [key]: e.value }))}
                                                />
                                            </div>
                                            {idx < NOTIF_ITEMS.length - 1 && <Divider style={{ margin: '0' }} />}
                                        </div>
                                    ))}
                                    <div className="sla-actions" style={{ marginTop: 14 }}>
                                        <Button label="Save Settings" icon="pi pi-save"
                                            loading={savingSettings} className="sla-btn-save"
                                            onClick={onSaveSettings} />
                                    </div>
                                </>
                            )}
                        </EssCard>

                        <EssCard title="Auto-Assignment Rules" icon="pi-arrow-right-arrow-left">
                            {loading ? <Skeleton height="120px" /> : (
                                <>
                                    {rules.length === 0 && (
                                        <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 12 }}>
                                            No categories found for this department. Add categories first.
                                        </div>
                                    )}
                                    {rules.map((rule, i) => (
                                        <div key={rule.categoryId ?? i} className="assign-row">
                                            <label className="assign-label">
                                                {rule.categoryName} &mdash; Assign to
                                            </label>
                                            <Dropdown
                                                value={rule.agentId}
                                                options={agents}
                                                onChange={(e) => updateRule(i, e.value)}
                                                placeholder="Select agent or Round Robin"
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    ))}
                                    {rules.length > 0 && (
                                        <Button label="Save Rules" icon="pi pi-save"
                                            loading={savingRules} className="sla-btn-save"
                                            style={{ marginTop: 8 }} onClick={onSaveRules} />
                                    )}
                                </>
                            )}
                        </EssCard>

                    </div>
                </div>
            )}
        </div>
    );
};

export default SLA;
